import React, {memo} from 'react';
import {OptionalInvoiceExportFieldStates} from './InvoicePDFTypes';
import {Document, Font, Page} from '@react-pdf/renderer';
import type {InvoicePDFDownloadModal_viewer$data as InvoicePDFDownloadModal_viewer} from './__generated__/InvoicePDFDownloadModal_viewer.graphql';
import {IntlShape} from 'react-intl';
import InvoicePDFHeader from './InvoicePDFHeader';
import InvoicePDFTable from './InvoicePDFTable';
import InvoicePDFFooter from './InvoicePDFFooter';
import InvoicePDFWorkDetail from './InvoicePDFWorkDetail';
import {hasFeatureFlag} from '../../../../../forecast-app/shared/util/FeatureUtil';

type InvoicePDFProps = {
	invoiceData: InvoicePDFDownloadModal_viewer['invoice'];
	companyData: InvoicePDFDownloadModal_viewer['company'];
	optionalFields: OptionalInvoiceExportFieldStates;
	intl: IntlShape;
	quantityDecimals: number;
};

// Done to avoid words being hyphenated in the PDF
Font.registerHyphenationCallback(word => [word]);

const InvoicePDF = memo(({invoiceData, companyData, optionalFields, intl, quantityDecimals}: InvoicePDFProps): JSX.Element => {
	return (
		<Document>
			<Page size="A4" style={[{paddingVertical: '32px', paddingHorizontal: '41px'}]}>
				<InvoicePDFHeader
					invoiceData={invoiceData}
					companyData={companyData}
					intl={intl}
					optionalFields={optionalFields}
				/>
				<InvoicePDFTable invoiceData={invoiceData} intl={intl} />
				{/* After the table, so it lands below Balance due, and before the footer, whose leading
				    flexGrow spacer pins Notes to the bottom of the page. Rates are disclosed only when
				    the company has opted in.

				    The flag is checked here as well as on the export checkbox that sets
				    timeRegistrationsChecked. That is not redundant: this is the client-facing document,
				    and without it the only thing keeping work detail off an unflagged company's invoice
				    would be the initial state of a checkbox in a different component. */}
				{hasFeatureFlag('work_detail_on_fixed_price_invoices') && optionalFields.timeRegistrationsChecked && (
					<InvoicePDFWorkDetail
						invoiceData={invoiceData}
						intl={intl}
						showRates={!!companyData?.workDetailShowRates}
						quantityDecimals={quantityDecimals}
					/>
				)}
				{optionalFields.invoiceNotesChecked && <InvoicePDFFooter invoiceData={invoiceData} intl={intl} />}
			</Page>
		</Document>
	);
});

export default InvoicePDF;
